/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { TokenMetadata } from "../lib/pinata";

export interface MarketToken extends TokenMetadata {
    price: number;
    change24h: number;
    volume24h: number;
    marketCap: number;
    launchDate: number; // timestamp
    isHot?: boolean;
    // Fix: Add optional priceHistory to support sparkline charts
    priceHistory?: number[];
}

export const GLOBAL_STATS = {
    totalVolume: "312.5M",
    activePairs: 1850,
    dominance: 42, // % market dominance vs other launchpads
    totalLiquidity: "105.2M",
    totalUsers: "28.4K",
    tokensMinted: 15420,
    successfulGraduations: 892
};

export const MARKET_DOMINANCE_DATA = {
    solana: {
        tvl: 85400000,
        volume: 245000000,
        users: 18500
    },
    sui: {
        tvl: 19800000,
        volume: 67500000,
        users: 9900
    }
};

export const MARKET_DATA: MarketToken[] = [
    {
        id: 'hot-network-sol',
        name: "HOT NETWORK",
        symbol: "HOT",
        description: "The governance token for the HotLaunchpad ecosystem.",
        image: "/assets/hot-token.svg",
        header_image: "https://placehold.co/1200x400/1a1a1a/C5A059?text=HOT+HEADER",
        price: 2.15,
        change24h: 8.5,
        volume24h: 6500000,
        marketCap: 21000000,
        launchDate: Date.now() - 100000000,
        tags: ["Governance", "DAO"],
        properties: {
            chain: 'SOL',
            tokenStandard: 'SPL',
            mintAddress: 'HotDAO11111111111111111111111111111111111',
            isLive: true,
            aiAnalyticsEnabled: true,
        },
        isHot: true
    },
    {
        id: 'sui-pepe',
        name: "SuiPepe",
        symbol: "SPEPE",
        description: "The frog has arrived on Sui.",
        image: "https://placehold.co/400x400/2a9d8f/ffffff?text=PEPE",
        price: 0.00042,
        change24h: 85.2,
        volume24h: 1200000,
        marketCap: 420000,
        launchDate: Date.now() - 3600000,
        tags: ["Meme"],
        properties: {
            chain: 'SUI',
            tokenStandard: 'SUI_COIN',
            mintAddress: '0xSuiPepeAddress...',
            isLive: true,
            aiAnalyticsEnabled: true,
        },
        isHot: true
    },
    {
        id: 'quantum-sol',
        name: "Quantum Sol",
        symbol: "QSOL",
        description: "Quantum resistant ledger on Solana.",
        image: "https://placehold.co/400x400/4F46E5/ffffff?text=Q",
        price: 4.50,
        change24h: -2.3,
        volume24h: 850000,
        marketCap: 4500000,
        launchDate: Date.now() - 86400000 * 5,
        tags: ["Utility", "DeFi"],
        properties: {
            chain: 'SOL',
            tokenStandard: 'TOKEN_2022',
            mintAddress: 'QuantumSolAddress...',
            isLive: true,
            aiAnalyticsEnabled: true,
            extensions: {
                transferFeeBasisPoints: 200 // 2%
            }
        }
    },
    {
        id: 'void-walker-sol',
        name: "Void Walker",
        symbol: "VOID",
        description: "Privacy layer for the new internet.",
        image: "https://placehold.co/400x400/000000/ffffff?text=V",
        price: 0.85,
        change24h: 5.4,
        volume24h: 320000,
        marketCap: 850000,
        launchDate: Date.now() - 86400000 * 2,
        tags: ["Privacy", "DAO"],
        properties: {
            chain: 'SOL',
            tokenStandard: 'SPL',
            mintAddress: 'VoidWalkerAddress...',
            isLive: true,
            aiAnalyticsEnabled: true,
        }
    },
    {
        id: 'sui-nexus',
        name: "Sui Nexus",
        symbol: "SNEX",
        description: "Bridging realities on Sui.",
        image: "https://placehold.co/400x400/3a0ca3/ffffff?text=SNEX",
        price: 1.15,
        change24h: 1.2,
        volume24h: 150000,
        marketCap: 1150000,
        launchDate: Date.now() - 86400000 * 10,
        tags: ["Gaming", "Utility"],
        properties: {
            chain: 'SUI',
            tokenStandard: 'SUI_COIN',
            mintAddress: '0xSuiNexusAddress...',
            isLive: true,
            aiAnalyticsEnabled: false,
        }
    },
    {
        id: 'cyber-doge-sol',
        name: "Cyber Doge",
        symbol: "CYDOGE",
        description: "Much cyber, very future.",
        image: "https://placehold.co/400x400/fca311/000000?text=DOGE",
        price: 0.000023,
        change24h: -15.4,
        volume24h: 2100000,
        marketCap: 2300000,
        launchDate: Date.now() - 86400000 * 1,
        tags: ["Meme"],
        properties: {
            chain: 'SOL',
            tokenStandard: 'SPL',
            mintAddress: 'CyberDogeAddress...',
            isLive: true,
            aiAnalyticsEnabled: true,
        },
        isHot: true
    },
    {
        id: 'yield-tax-sol',
        name: "Yield Protocol",
        symbol: "YIELD",
        description: "Auto-staking protocol leveraging Token-2022 extensions.",
        image: "https://placehold.co/400x400/10b981/ffffff?text=YIELD",
        price: 1.05,
        change24h: 3.2,
        volume24h: 450000,
        marketCap: 1050000,
        launchDate: Date.now() - 86400000 * 4,
        tags: ["DeFi", "Yield"],
        properties: {
            chain: 'SOL',
            tokenStandard: 'TOKEN_2022',
            mintAddress: 'YieldProtocolToken2022...',
            isLive: true,
            aiAnalyticsEnabled: true,
            extensions: {
                transferFeeBasisPoints: 100, // 1%
                interestRate: 500 // 5% Interest
            }
        },
        isHot: true
    }
];

export const RECENT_LAUNCHES: MarketToken[] = [
    {
        id: 'neural-net-sol',
        name: "Neural Net",
        symbol: "NEURA",
        description: "AI training data on-chain.",
        image: "https://placehold.co/400x400/e63946/ffffff?text=AI",
        price: 0.0,
        change24h: 0,
        volume24h: 0,
        marketCap: 0,
        launchDate: Date.now() + 86400000 * 1, // Future
        tags: ["AI", "Data"],
        properties: { 
            chain: 'SOL',
            isLive: false,
            aiAnalyticsEnabled: false,
        }
    },
    {
        id: 'eco-credits-sui',
        name: "EcoCredits",
        symbol: "ECO",
        description: "Carbon credits for the metaverse.",
        image: "https://placehold.co/400x400/2a9d8f/ffffff?text=ECO",
        price: 0.0,
        change24h: 0,
        volume24h: 0,
        marketCap: 0,
        launchDate: Date.now() + 86400000 * 3, // Future
        tags: ["Social", "Utility"],
        properties: { 
            chain: 'SUI',
            isLive: false,
            aiAnalyticsEnabled: false,
        }
    }
];